import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BarChart3, TrendingUp, Users, MessageSquare, Calendar, Activity, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FrequencyDashboard90 = () => {
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30'); // Últimos 30 dias
  const [stats, setStats] = useState({
    totalQuickFeedbacks: 0,
    totalCheckins: 0,
    totalAtividades: 0,
    quickFeedbacksPorTipo: [],
    atividadesPorDia: [],
    topColaboradores: []
  });

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  useEffect(() => {
    loadDashboardData();
  }, [period]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Buscar Quick Feedbacks
      const quickFeedbacksResponse = await axios.get(`${API}/api/feedback90/quick/historico`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const quickFeedbacks = quickFeedbacksResponse.data || [];

      // Buscar Check-ins 1:1
      const checkinsResponse = await axios.get(`${API}/api/feedback90/1on1/historico`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const checkins = checkinsResponse.data || [];

      // Filtrar por período
      const dataLimite = new Date();
      dataLimite.setDate(dataLimite.getDate() - parseInt(period));

      const quickFeedbacksFiltrados = quickFeedbacks.filter(f => {
        const dataCriacao = new Date(f.criado_em || f.created_at);
        return dataCriacao >= dataLimite;
      });

      const checkinsFiltrados = checkins.filter(c => {
        const dataCriacao = new Date(c.criado_em || c.data_hora);
        return dataCriacao >= dataLimite;
      });

      // Estatísticas gerais
      const totalQuickFeedbacks = quickFeedbacksFiltrados.length;
      const totalCheckins = checkinsFiltrados.length;
      const totalAtividades = totalQuickFeedbacks + totalCheckins;

      // Quick Feedbacks por tipo
      const tiposCount = {};
      quickFeedbacksFiltrados.forEach(f => {
        const tipo = f.tipo || 'kudos';
        tiposCount[tipo] = (tiposCount[tipo] || 0) + 1;
      });

      const quickFeedbacksPorTipo = Object.keys(tiposCount).map(tipo => ({
        name: tipo === 'kudos' ? 'Kudos' : tipo === 'continue' ? 'Continue Fazendo' : 'Melhoria',
        value: tiposCount[tipo],
        fill: tipo === 'kudos' ? '#10b981' : tipo === 'continue' ? '#3b82f6' : '#f59e0b'
      }));

      // Atividades por dia (últimos dias do período)
      const diasMap = {};
      const hoje = new Date();
      
      for (let i = parseInt(period) - 1; i >= 0; i--) {
        const data = new Date(hoje);
        data.setDate(data.getDate() - i);
        const dataStr = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        diasMap[dataStr] = { quickFeedbacks: 0, checkins: 0 };
      }

      quickFeedbacksFiltrados.forEach(f => {
        const data = new Date(f.criado_em || f.created_at);
        const dataStr = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        if (diasMap[dataStr]) {
          diasMap[dataStr].quickFeedbacks++;
        }
      });

      checkinsFiltrados.forEach(c => {
        const data = new Date(c.criado_em || c.data_hora);
        const dataStr = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        if (diasMap[dataStr]) {
          diasMap[dataStr].checkins++;
        }
      });

      const atividadesPorDia = Object.keys(diasMap).map(dia => ({
        dia,
        'Quick Feedbacks': diasMap[dia].quickFeedbacks,
        'Check-ins 1:1': diasMap[dia].checkins
      }));

      // Top colaboradores (quem mais enviou feedbacks/checkins)
      const colaboradoresMap = {};
      
      [...quickFeedbacksFiltrados, ...checkinsFiltrados].forEach(item => {
        const nome = item.de_usuario_nome || 'Desconhecido';
        if (!colaboradoresMap[nome]) {
          colaboradoresMap[nome] = { nome, total: 0 };
        }
        colaboradoresMap[nome].total++;
      });

      const topColaboradores = Object.values(colaboradoresMap)
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);

      setStats({
        totalQuickFeedbacks,
        totalCheckins,
        totalAtividades,
        quickFeedbacksPorTipo,
        atividadesPorDia,
        topColaboradores
      });

    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="h-8 w-8 mr-3 text-blue-600" />
            Dashboard 90º
          </h1>
          <p className="text-gray-600 mt-1">
            Métricas e estatísticas de Quick Feedbacks e Check-ins 1:1
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7">Últimos 7 dias</option>
            <option value="15">Últimos 15 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="60">Últimos 60 dias</option>
            <option value="90">Últimos 90 dias</option>
          </select>
          <button
            onClick={loadDashboardData}
            disabled={loading}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Quick Feedbacks</CardTitle>
            <MessageSquare className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.totalQuickFeedbacks}</div>
            <p className="text-xs text-gray-500 mt-1">Feedbacks rápidos enviados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Check-ins 1:1</CardTitle>
            <Users className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.totalCheckins}</div>
            <p className="text-xs text-gray-500 mt-1">Conversas individuais realizadas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total de Atividades</CardTitle>
            <Activity className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.totalAtividades}</div>
            <p className="text-xs text-gray-500 mt-1">Feedbacks + Check-ins</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Pizza - Quick Feedbacks por Tipo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              <span>Quick Feedbacks por Tipo</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.quickFeedbacksPorTipo.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.quickFeedbacksPorTipo}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.quickFeedbacksPorTipo.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                Nenhum dado disponível
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Colaboradores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <span>Top 5 Colaboradores Mais Ativos</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.topColaboradores.length > 0 ? (
              <div className="space-y-3">
                {stats.topColaboradores.map((colab, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-yellow-500' : 
                        index === 1 ? 'bg-gray-400' : 
                        index === 2 ? 'bg-orange-600' : 
                        'bg-blue-500'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="font-medium text-gray-700">{colab.nome}</span>
                    </div>
                    <span className="text-lg font-bold text-gray-900">{colab.total}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                Nenhum dado disponível
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Linhas - Atividades por Dia */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-purple-600" />
            <span>Atividades por Dia</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.atividadesPorDia.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={stats.atividadesPorDia}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dia" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="Quick Feedbacks" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Check-ins 1:1" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ fill: '#10b981' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Nenhum dado disponível
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FrequencyDashboard90;
